import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Heart, Target, Globe, Lightbulb, HandHeart, Award, Zap, ArrowRight, Star } from "lucide-react";

function Story() {
  const [isVisible, setIsVisible] = useState({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    "img/kccathand.jpg",
    "img/KccHand.jpg", 
    "img/unemployment.jpg",
    "img/respect.jpg"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    // Auto-rotate images
    const imageInterval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 4000);

    return () => {
      observer.disconnect();
      clearInterval(imageInterval);
    };
  }, []);

  const values = [
    {
      icon: Heart,
      title: "Community First",
      description: "Every decision we make puts our community's wellbeing at the center",
      color: "from-red-500 to-pink-500",
      bgColor: "from-red-50 to-pink-50"
    },
    {
      icon: Users,
      title: "Inclusive Collaboration", 
      description: "Working together with diverse groups to create sustainable solutions",
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-50 to-cyan-50"
    },
    {
      icon: Lightbulb,
      title: "Innovation & Education",
      description: "Empowering through knowledge and fostering creative problem-solving",
      color: "from-green-500 to-emerald-500",
      bgColor: "from-green-50 to-emerald-50"
    },
    {
      icon: Target,
      title: "Results-Driven",
      description: "Measurable impact that creates lasting change in our communities",
      color: "from-purple-500 to-indigo-500",
      bgColor: "from-purple-50 to-indigo-50"
    }
  ];

  const milestones = [
    {
      year: "2018",
      title: "Foundation",
      description: "KCC was born from a vision to transform marginalized communities",
      icon: Star
    },
    {
      year: "2019",
      title: "First Programs",
      description: "Launched HIV/AIDS awareness and youth empowerment initiatives",
      icon: Zap
    },
    {
      year: "2021",
      title: "Regional Expansion",
      description: "Extended operations to Kayunga, Jinja, and Mukono districts",
      icon: Globe
    },
    {
      year: "2024",
      title: "Growing Impact",
      description: "Reached 814+ youth and established sustainable community programs",
      icon: Award
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 overflow-hidden">
      <Header />
      
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-5 sm:left-10 w-48 sm:w-96 h-48 sm:h-96 bg-purple-200/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-5 sm:right-10 w-40 sm:w-80 h-40 sm:h-80 bg-pink-200/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-32 sm:w-64 h-32 sm:h-64 bg-blue-200/20 rounded-full blur-xl animate-pulse delay-2000"></div>
        
        {/* Floating organic shapes */}
        <div className="absolute top-1/3 right-0 w-[250px] sm:w-[500px] h-[250px] sm:h-[500px] opacity-10">
          <svg viewBox="0 0 200 200" className="w-full h-full text-purple-300 animate-spin" style={{animationDuration: '25s'}}>
            <path fill="currentColor" d="M44.7,-76.4C58.8,-69.2,71.8,-57.1,79.6,-41.8C87.4,-26.6,90,-8.2,87.4,9.1C84.8,26.4,77,42.6,63.2,53.8C49.4,65,29.6,71.2,8.9,76.3C-11.8,81.4,-33.4,85.4,-49.8,78.1C-66.2,70.8,-77.4,52.2,-84.1,31.1C-90.8,10,-93,13.5,-89.7,-8.8C-86.4,-31.1,-77.6,-58.2,-61.2,-65.4C-44.8,-72.6,-20.8,-59.9,-0.1,-59.8C20.6,-59.7,30.6,-83.6,44.7,-76.4Z"/>
          </svg>
        </div>
        
        <div className="absolute bottom-1/4 left-0 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] opacity-15">
          <svg viewBox="0 0 200 200" className="w-full h-full text-pink-300 animate-spin" style={{animationDuration: '30s', animationDirection: 'reverse'}}>
            <path fill="currentColor" d="M37.1,-66.5C48.3,-58.9,57.8,-46.8,64.4,-33.1C71,-19.4,74.7,-4.1,73.9,10.7C73.1,25.5,67.8,39.8,58.2,51.2C48.6,62.6,34.7,71.1,19.2,75.8C3.7,80.5,-13.4,81.4,-28.9,76.9C-44.4,72.4,-58.3,62.5,-68.7,49.1C-79.1,35.7,-86,19.8,-87.4,3.2C-88.8,-13.4,-84.7,-30.6,-75.1,-44.2C-65.5,-57.8,-50.4,-67.8,-34.5,-72.4C-18.6,-77,-3.9,-76.2,9.7,-70.8C23.3,-65.4,25.9,-74.1,37.1,-66.5Z"/>
          </svg>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative pt-32 sm:pt-32 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-purple-100 text-purple-800 px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8 backdrop-blur-sm border border-purple-200">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Our Journey Since 2018
            </div>
            
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-black mb-6 sm:mb-8">
              <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent">
                Our Story
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-4">
              From humble beginnings to transformative impact—discover how Kayunga Community Club 
              became a beacon of hope for marginalized communities across Uganda.
            </p>
          </div>
        </div>
      </div>

      {/* Who We Are Section */}
      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <div 
              id="who-we-are-content"
              data-animate
              className={`transform transition-all duration-1000 ${
                isVisible['who-we-are-content'] ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'
              }`}
            >
              <div className="relative">
                {/* Glassmorphism container */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-white/30 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/20 shadow-2xl transform rotate-1"></div>
                <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 border border-white/30 shadow-xl">
                  <div className="inline-flex items-center bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                    <Target className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                    Who We Are
                  </div>
                  
                  <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8 leading-tight">
                    Building <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Communities</span> of Hope
                  </h2>
                  
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4 sm:mb-6">
                    <strong className="text-purple-700">Kayunga Community Club</strong> is a registered community-based organization 
                    dedicated to fostering and promoting good friendly relations, promoting development, 
                    hygiene, education, innovation and awareness.
                  </p>
                  
                  <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
                    We are committed to supporting men, women, and youths through the active community 
                    of Kayunga and neighborhoods in order to achieve sustainable future development.
                  </p>

                  <div className="mt-6 sm:mt-8 grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">814+</div>
                      <div className="text-xs sm:text-sm text-gray-600">Youth Reached</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl sm:text-3xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">3</div>
                      <div className="text-xs sm:text-sm text-gray-600">Districts</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Interactive Image Gallery */}
            <div 
              id="who-we-are-images"
              data-animate
              className={`transform transition-all duration-1000 delay-300 ${
                isVisible['who-we-are-images'] ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
              }`}
            >
              <div className="relative">
                {/* Main image display with glassmorphism frame */}
                <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-10"></div>
                  <img
                    src={images[currentImageIndex]}
                    alt="Community Impact"
                    className="w-full h-64 sm:h-80 md:h-96 object-cover transition-opacity duration-1000"
                  />
                  
                  {/* Floating glassmorphic info card */}
                  <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6 bg-white/20 backdrop-blur-xl rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-white/30 z-20">
                    <div className="text-white font-semibold text-sm sm:text-base">Our Impact in Action</div>
                    <div className="text-white/80 text-xs sm:text-sm">Building stronger communities together</div>
                  </div>
                </div>
                
                {/* Image thumbnails */}
                <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6 justify-center">
                  {images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative w-12 h-12 sm:w-16 sm:h-16 rounded-lg sm:rounded-xl overflow-hidden transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'ring-2 sm:ring-4 ring-purple-500 scale-110' 
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img
                        src={image}
                        alt={`Community ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Values Section */}
      <div className="py-16 sm:py-20 bg-white/60 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="values-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['values-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4 sm:mb-6">
              Our Core <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Values</span>
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  id={`value-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`value-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="group relative h-full">
                    {/* Glassmorphic card */}
                    <div className="absolute inset-0 bg-white/40 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 shadow-lg transform group-hover:scale-105 transition-all duration-300"></div>
                    <div className="relative p-6 sm:p-8 h-full flex flex-col">
                      <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${value.color} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 mx-auto transform group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                      </div>
                      
                      <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-3 sm:mb-4 text-center">{value.title}</h3>
                      <p className="text-sm sm:text-base text-gray-700 text-center flex-grow leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Our Approach Section */}
      <div className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="approach-section"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['approach-section'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="relative">
              {/* Large glassmorphic container */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-white/30 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/20 shadow-2xl"></div>
              <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-16 border border-white/30 shadow-xl">
                <div className="text-center mb-8 sm:mb-12">
                  <div className="inline-flex items-center bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold mb-6 sm:mb-8">
                    <HandHeart className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    Our Family-Centered Approach
                  </div>
                  
                  <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-6 sm:mb-8">
                    Solutions Through <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Collaboration</span>
                  </h2>
                </div>
                
                <div className="max-w-4xl mx-auto">
                  <div className="relative">
                    <div className="absolute -left-3 sm:-left-6 top-0 w-0.5 sm:w-1 h-full bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></div>
                    
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed pl-6 sm:pl-12 mb-6 sm:mb-8">
                      <strong className="text-green-700">Our service philosophy</strong> is shaped by our experience in 
                      community service since we started. We believe that our communities always need service and solutions.
                    </p>
                    
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 leading-relaxed pl-6 sm:pl-12">
                      As a community-based organization, we do not offer "service" only but we also work with other 
                      service groups to ensure that there is a solution to every problem in our communities with 
                      emphasis in <strong className="text-blue-700">improving, building and developing</strong> our communities of operation.
                    </p>
                  </div>

                  {/* Call to action */}
                  <div className="mt-8 sm:mt-12 text-center">
                    <div className="inline-flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-gradient-to-r from-green-100 via-blue-100 to-purple-100 px-6 sm:px-8 py-4 rounded-xl sm:rounded-2xl border border-green-200">
                      <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
                      <span className="text-base sm:text-lg md:text-xl font-semibold text-gray-800 text-center">
                        Building sustainable solutions for lasting community impact
                      </span>
                      <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="py-16 sm:py-20 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            id="timeline-header"
            data-animate
            className={`text-center mb-12 sm:mb-16 transform transition-all duration-1000 ${
              isVisible['timeline-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
              Our Journey of Impact
            </h2>
            <div className="w-24 sm:w-32 h-1 bg-white rounded-full mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              return (
                <div
                  key={index}
                  id={`milestone-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 300} ${
                    isVisible[`milestone-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="group relative">
                    {/* Glassmorphic timeline card */}
                    <div className="bg-white/20 backdrop-blur-xl rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-white/30 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 text-center h-full">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/30 backdrop-blur-sm rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                      </div>
                      
                      <div className="text-2xl sm:text-3xl font-black text-white mb-2">{milestone.year}</div>
                      <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">{milestone.title}</h3>
                      <p className="text-sm sm:text-base text-white/90 leading-relaxed">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Floating particles animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 sm:w-3 sm:h-3 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-20 animate-ping`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${i * 3}s`,
              animationDuration: `${5 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      <Footer />
    </div>
  );
}

export default Story;